/**
 * HeroBackground3D - Full-viewport 3D line-surface hero background.
 *
 * Renders a stereo "wireframe + translucent shell" double-layer wave surface
 * that evokes an ice-blue tech snow-slope. Uses raw Three.js (imperative,
 * matching the existing Pressure3DField pattern) — no react-three-fiber.
 *
 * Layers:
 *   1. Wireframe layer (glowing ice-blue lines) — the "3D line surface" skeleton.
 *   2. Translucent solid layer (~0.18 opacity) — fills volume so the surface
 *      reads as 3D depth rather than a hollow wireframe.
 *
 * Interaction:
 *   - Mouse parallax: camera lerps toward a small offset derived from pointer
 *     position (only the camera moves; no per-frame vertex recompute).
 *   - Scroll deformation: as the page scrolls through the hero (0 → 100vh),
 *     wave amplitude + camera pitch interpolate, so the surface "rises /
 *     advances" into the next section.
 *
 * Robustness (this is first-screen, non-lazy-mounted, must be stable):
 *   - Low poly: PlaneGeometry 48×48 (~2300 verts), doubled ≈ 4600 verts.
 *   - Pauses rAF when the hero leaves the viewport (IntersectionObserver).
 *   - Honors `prefers-reduced-motion` → renders a single static frame.
 *   - DPR capped at 2.
 *   - Full dispose() of geometry/material/renderer + listener teardown.
 *   - WebGL context-lost / unavailable → the component renders nothing and
 *     the HeroSection's CSS gradient shows through as a graceful fallback.
 *
 * 全屏 3D 线条曲面 Hero 背景：线框 + 半透明实体双层冰蓝波形面，配合鼠标
 * 视差与滚动形变，呈现立体雪坡纵深感。采用原生 Three.js 命令式写法（与
 * Pressure3DField 同款），不引入 react-three-fiber。
 */
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

// ----- Config -----
// 配置常量

const GRID_W = 48; // 平面分段数（横向）
const GRID_H = 48; // 平面分段数（纵向），48×48 ≈ 2301 顶点
const PLANE_W = 240; // 世界空间平面宽度
const PLANE_D = 360; // 世界空间平面深度（Y 旋转后变 Z）
const WAVE_AMP_BASE = 11; // 基础波形振幅
const WAVE_AMP_SCROLL = 16; // 滚动到 100vh 时叠加的额外振幅
const PARALLALL_X = 14; // 鼠标视差横向最大偏移（世界单位）
const PARALLALL_Y = 9; // 鼠标视差纵向最大偏移
const LERP = 0.06; // 视差/形变平滑系数

const COLOR_ICE = 0x22d3ee; // --color-ice
const COLOR_BASE = 0x0a0a0f; // --bg-base（Fog 与背景融合色）

/**
 * Build the wave surface geometry once. The plane lies in the XZ plane after
 * a -90° X rotation (Y = up = wave height). The geometry is rewritten in
 * place each frame via a shared BufferAttribute (no per-frame allocation).
 *
 * 一次性创建波形几何体。经 -90° X 旋转后平面落在 XZ 平面（Y 轴向上=波高）。
 * 每帧只就地改写 BufferAttribute，不重新分配几何体。
 */
function createWaveGeometry(): THREE.PlaneGeometry {
  const geo = new THREE.PlaneGeometry(PLANE_W, PLANE_D, GRID_W, GRID_H);
  geo.rotateX(-Math.PI / 2);
  return geo;
}

/**
 * Compute wave height at a grid vertex for time `t` and amplitude `amp`.
 * A sum of two sinusoids + a slow diagonal swell produces gentle, organic
 * ridge-like motion reminiscent of snow-slope terrain.
 *
 * 由时间 t 与振幅 amp 计算某顶点的波形高度：两组正弦 + 缓慢对角起伏，
 * 营造类似雪坡地形的柔和连绵起伏。
 */
function waveHeight(x: number, z: number, t: number, amp: number): number {
  const w1 = Math.sin(x * 0.018 + t * 0.7) * Math.cos(z * 0.014 + t * 0.5);
  const w2 = Math.sin((x + z) * 0.01 - t * 0.4) * 0.5;
  const swell = Math.sin(z * 0.006 + t * 0.2) * 0.8;
  return (w1 + w2 + swell) * amp;
}

/**
 * Rewrite the geometry's Y positions in place from the wave function, then
 * flag the attribute for GPU upload and recompute normals (so the solid
 * layer shades correctly).
 *
 * 就地改写几何体 Y 坐标并标记上传、重算法线（让实体层着色正确）。
 */
function applyWave(
  geo: THREE.PlaneGeometry,
  t: number,
  amp: number,
  drift: number,
): void {
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const arr = pos.array as Float32Array;
  for (let i = 0; i < pos.count; i++) {
    const x = arr[i * 3];
    const z = arr[i * 3 + 2];
    // `drift` offsets the Z input to the wave function, producing endless
    // forward motion WITHOUT translating the geometry (which would accumulate
    // and push vertices to infinity over time).
    // drift 偏移波形的 Z 输入，产生连绵前移，无需平移几何体（平移会累积至发散）。
    pos.setY(i, waveHeight(x, z + drift, t, amp));
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
}

export default function HeroBackground3D() {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // Respect reduced-motion: render one static frame, skip animation.
    // 尊重「减少动效」：只渲染一帧静态画面，不跑动画。
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    // ---- Renderer ----
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      // WebGL unsupported → render nothing; CSS gradient fallback shows.
      // WebGL 不可用 → 不渲染，回退到 CSS 渐变背景。
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setClearColor(0x000000, 0); // transparent — gradient shows through
    mount.appendChild(renderer.domElement);

    // ---- Scene + fog (melt into the dark hero gradient) ----
    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(COLOR_BASE, 120, 360);

    // ---- Camera (slightly elevated, looking down the slope) ----
    const camera = new THREE.PerspectiveCamera(
      52,
      mount.clientWidth / mount.clientHeight,
      0.1,
      1000,
    );
    const camHome = new THREE.Vector3(0, 34, 120);
    camera.position.copy(camHome);
    camera.lookAt(0, -6, -40);

    // ---- Wave geometry (shared by both layers) ----
    const geo = createWaveGeometry();

    // Wireframe layer: glowing ice-blue lines (the "3D line surface" skeleton).
    // 线框层：冰蓝发光线条，构成 3D 线条曲面骨架。
    const wireMaterial = new THREE.MeshBasicMaterial({
      color: COLOR_ICE,
      wireframe: true,
      transparent: true,
      opacity: 0.42,
      fog: true,
    });
    const wireMesh = new THREE.Mesh(geo, wireMaterial);

    // Translucent solid layer: fills volume so the surface reads as 3D depth.
    // 半透明实体层：填充体积感，使曲面呈现立体纵深。
    const solidMaterial = new THREE.MeshStandardMaterial({
      color: COLOR_ICE,
      transparent: true,
      opacity: 0.16,
      side: THREE.DoubleSide,
      metalness: 0.2,
      roughness: 0.7,
      flatShading: false,
      fog: true,
    });
    const solidMesh = new THREE.Mesh(geo, solidMaterial);

    // A subtle ridge accent (deeper ice) layered for extra depth cue.
    // 一层更深的冰色脊线点缀，加强纵深层次。
    const ridgeMaterial = new THREE.MeshBasicMaterial({
      color: 0x3b82f6,
      wireframe: true,
      transparent: true,
      opacity: 0.12,
      fog: true,
    });
    const ridgeMesh = new THREE.Mesh(geo, ridgeMaterial);
    ridgeMesh.position.y = -6; // slightly below, parallax on scroll

    scene.add(wireMesh, solidMesh, ridgeMesh);

    // ---- Lighting for the solid layer ----
    scene.add(new THREE.AmbientLight(0x445577, 0.8));
    const key = new THREE.DirectionalLight(0xffffff, 0.9);
    key.position.set(60, 120, 80);
    scene.add(key);

    // ---- Interaction state ----
    const target = { px: 0, py: 0, amp: WAVE_AMP_BASE, pitch: camHome.y };
    const cur = { px: 0, py: 0, amp: WAVE_AMP_BASE, pitch: camHome.y };

    const onMouseMove = (e: MouseEvent) => {
      // Normalize pointer to [-1, 1] over the viewport.
      // 指针归一化到 [-1, 1]。
      target.px = (e.clientX / window.innerWidth) * 2 - 1;
      target.py = (e.clientY / window.innerHeight) * 2 - 1;
    };
    const onScroll = () => {
      // 0 at top of hero → 1 at one viewport scrolled.
      // 处于 hero 顶部为 0，滚出一屏为 1。
      const p = Math.min(1, Math.max(0, window.scrollY / window.innerHeight));
      target.amp = WAVE_AMP_BASE + p * WAVE_AMP_SCROLL;
      target.pitch = camHome.y + p * 18; // camera rises, slope advances
    };

    // ---- Visibility-gated rAF (pause when hero offscreen) ----
    let visible = true;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visible = entry.isIntersecting;
        }
      },
      { threshold: 0 },
    );
    io.observe(mount);

    // ---- Resize handling ----
    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);

    // ---- WebGL context-lost fallback ----
    let contextLost = false;
    const handleContextLost = (event: Event) => {
      event.preventDefault();
      contextLost = true;
    };
    renderer.domElement.addEventListener('webglcontextlost', handleContextLost);

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    // ---- Static frame for reduced-motion ----
    if (reducedMotion) {
      applyWave(geo, 0, WAVE_AMP_BASE, 0);
      renderer.render(scene, camera);
    }

    // ---- Animation loop ----
    let rafId = 0;
    const clock = new THREE.Clock();

    const animate = () => {
      rafId = requestAnimationFrame(animate);
      if (contextLost || !visible) return;

      const t = clock.getElapsedTime();

      // Smoothly chase interaction targets (avoid jitter).
      // 平滑追赶交互目标值，避免抖动。
      cur.px += (target.px - cur.px) * LERP;
      cur.py += (target.py - cur.py) * LERP;
      cur.amp += (target.amp - cur.amp) * LERP;
      cur.pitch += (target.pitch - cur.pitch) * LERP;

      // Camera parallax from pointer + pitch from scroll.
      // 相机视差来自指针，俯角来自滚动。
      camera.position.x = camHome.x + cur.px * PARALLALL_X;
      camera.position.y = cur.pitch + cur.py * PARALLALL_Y;
      camera.lookAt(0, -6 - cur.py * 4, -40);

      // Advance the wave: phase over time + slow Z drift for endless motion.
      // 推进波形：时间相位推进 + Z 向缓慢漂移，形成连绵运动。
      applyWave(geo, t, cur.amp, t * 6);

      renderer.render(scene, camera);
    };
    if (!reducedMotion) animate();

    // ---- Cleanup ----
    return () => {
      cancelAnimationFrame(rafId);
      io.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('scroll', onScroll);
      renderer.domElement.removeEventListener(
        'webglcontextlost',
        handleContextLost,
      );
      geo.dispose();
      wireMaterial.dispose();
      solidMaterial.dispose();
      ridgeMaterial.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={mountRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
}
